"use client";

import React, { useState, useEffect } from "react";
import { ArrowRight, Check, Mail } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
type WvcLang = "en" | "zh" | "fr" | "es" | "de";
const WVC_LANG_KEY = "wvc_language";
const WVC_LANG_EVENT = "wvc_language_change";
function getWvcLanguage(): WvcLang {
  if (typeof window === "undefined") return "en";
  try {
    const saved = localStorage.getItem(WVC_LANG_KEY) as WvcLang | null;
    if (saved && ["en", "zh", "fr", "es", "de"].includes(saved)) {
      return saved;
    }
  } catch {}
  return "en";
}
function useWvcLanguage(): WvcLang {
  const [lang, setLang] = useState<WvcLang>(getWvcLanguage);
  useEffect(() => {
    const handleLangChange = () => {
      setLang(getWvcLanguage());
    };
    window.addEventListener(WVC_LANG_EVENT, handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener(WVC_LANG_EVENT, handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  return lang;
}
const translations = {
  en: {
    newsletter: "Newsletter",
    heading: "Stay In The Know",
    description: "Sign up to be the first to hear about new arrivals, exclusive offers and styling inspiration.",
    emailPlaceholder: "Enter your email address",
    subscribing: "Subscribing...",
    subscribe: "Subscribe",
    thankYou: "Thank you for subscribing",
    added: "We have added",
    toList: "to our private mailing list.",
    privacyText: "By subscribing, you agree to our Privacy Policy. Unsubscribe at any time."
  },
  zh: {
    newsletter: "新闻通讯",
    heading: "了解最新信息",
    description: "注册成为首批听到新品上市、独家优惠和造型灵感的人。",
    emailPlaceholder: "输入你的电子邮件地址",
    subscribing: "订阅中...",
    subscribe: "订阅",
    thankYou: "感谢您的订阅",
    added: "我们已添加",
    toList: "到我们的私人邮件列表。",
    privacyText: "通过订阅，您同意我们的隐私政策。随时取消订阅。"
  },
  fr: {
    newsletter: "Infolettre",
    heading: "Restez Informé",
    description: "Inscrivez-vous pour être le premier à entendre parler des nouvelles arrivées, des offres exclusives et de l'inspiration en matière de style.",
    emailPlaceholder: "Entrez votre adresse e-mail",
    subscribing: "Inscription en cours...",
    subscribe: "S'abonner",
    thankYou: "Merci de vous être abonné",
    added: "Nous avons ajouté",
    toList: "à notre liste de diffusion privée.",
    privacyText: "En vous abonnant, vous acceptez notre Politique de Confidentialité. Vous pouvez vous désabonner à tout moment."
  },
  es: {
    newsletter: "Boletín",
    heading: "Manténgase Informado",
    description: "Regístrate para ser el primero en enterarte de nuevas llegadas, ofertas exclusivas e inspiración en el estilo.",
    emailPlaceholder: "Ingresa tu dirección de correo electrónico",
    subscribing: "Suscripción en curso...",
    subscribe: "Suscribirse",
    thankYou: "Gracias por suscribirse",
    added: "Hemos agregado",
    toList: "a nuestra lista de correo privada.",
    privacyText: "Al suscribirse, aceptas nuestra Política de Privacidad. Puedes darte de baja en cualquier momento."
  },
  de: {
    newsletter: "Newsletter",
    heading: "Bleiben Sie informiert",
    description: "Melden Sie sich an, um als Erster von neuen Ankünften, exklusiven Angeboten und Styling-Inspiration zu erfahren.",
    emailPlaceholder: "Geben Sie Ihre E-Mail-Adresse ein",
    subscribing: "Wird abonniert...",
    subscribe: "Abonnieren",
    thankYou: "Danke fürs Abonnieren",
    added: "Wir haben hinzugefügt",
    toList: "auf unsere private Mailingliste.",
    privacyText: "Durch das Abonnieren stimmen Sie unserer Datenschutzrichtlinie zu. Jederzeit abmelden."
  }
};
export default function Subscribe() {
  const language = useWvcLanguage();
  const t = translations[language as keyof typeof translations] || translations.en;
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim() || isLoading) return;
    setIsLoading(true);
    // Simulate lightweight client submission feedback
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
    }, 600);
  };
  return <section id="邮件订阅" data-nav="light" className="relative w-full bg-background text-foreground">
      {/* Top Hairline Rule */}
      <Separator orientation="horizontal" className="bg-border opacity-70" />

      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20 md:px-10 lg:py-24">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-40px"
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="mx-auto max-w-2xl text-center">
          {/* Eyebrow Motif */}
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="h-px w-6 bg-border" />
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t.newsletter}
            </span>
            <span className="h-px w-6 bg-border" />
          </div>

          {/* Editorial Serif Headline */}
          <h2 className="font-serif text-3xl font-normal tracking-tight sm:text-4xl lg:text-5xl text-foreground">
            {t.heading}
          </h2>

          {/* Invitation Text */}
          <p className="mt-4 font-sans text-base font-light leading-relaxed text-muted-foreground sm:text-lg">
            {t.description}
          </p>

          {/* Subscription Form / Confirmation */}
          <div className="mt-8 sm:mt-10">
            {isSubmitted ? <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3
          }} className="flex flex-col items-center justify-center gap-2 border border-border bg-card p-6 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Check className="h-5 w-5" />
                </div>
                <h3 className="font-serif text-xl font-normal text-foreground">
                  {t.thankYou}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t.added} <span className="font-medium text-foreground">{email}</span> {t.toList}
                </p>
              </motion.div> : <form onSubmit={handleSubmit} className="mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center sm:gap-0">
                <div className="relative flex-1">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder={t.emailPlaceholder} aria-label="Email address" className="h-12 w-full rounded-none border border-input bg-background pl-11 pr-4 text-sm text-foreground shadow-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring sm:border-r-0" />
                </div>
                <Button type="submit" disabled={isLoading} className="h-12 rounded-none bg-primary px-8 text-sm font-medium tracking-wide text-primary-foreground shadow-none transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
                  {isLoading ? t.subscribing : <span className="inline-flex items-center gap-2">
                      {t.subscribe}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                    </span>}
                </Button>
              </form>}
          </div>

          {/* Privacy Reassurance */}
          <p className="mt-4 text-xs tracking-normal text-muted-foreground">
            {t.privacyText}
          </p>
        </motion.div>
      </div>

      {/* Bottom Hairline Rule */}
      <Separator orientation="horizontal" className="bg-border opacity-70" />
    </section>;
}